import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to manage Calendly script loading and event listeners
 * @param {Function} onEventScheduled - Callback when booking is completed
 * @returns {Object} { isCalendlyLoaded, openCalendly }
 */
const useCalendly = (onEventScheduled) => {
    const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false);
    const callbackRef = useRef(onEventScheduled);

    // Keep the ref updated with the latest callback
    useEffect(() => {
        callbackRef.current = onEventScheduled;
    }, [onEventScheduled]);

    useEffect(() => {
        // Check if Calendly script is already loaded
        if (window.Calendly) {
            setIsCalendlyLoaded(true);
            return;
        }

        // Load Calendly script dynamically
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        script.onload = () => setIsCalendlyLoaded(true);
        document.body.appendChild(script);

        // Load Calendly CSS
        const link = document.createElement('link');
        link.href = 'https://assets.calendly.com/assets/external/widget.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        // Removed cleanup function to persist script across navigations
    }, []);

    useEffect(() => {
        // Listen for Calendly events - registered once with empty dependency array
        const handleCalendlyEvent = (e) => {
            if (e.data.event === 'calendly.event_scheduled') {
                if (callbackRef.current) {
                    callbackRef.current(e.data.payload);
                }
            }
        };

        window.addEventListener('message', handleCalendlyEvent);

        return () => {
            window.removeEventListener('message', handleCalendlyEvent);
        };
    }, []); // Empty dependency array - listener registered only once

    /**
     * Opens Calendly popup widget
     * @param {string} url - Your Calendly scheduling URL
     * @param {Object} options - Additional Calendly options (prefill, utm, etc.)
     */
    const openCalendly = (url, options = {}) => {
        if (window.Calendly && isCalendlyLoaded) {
            window.Calendly.initPopupWidget({ url, ...options });
        } else {
            console.error('Calendly is not loaded yet');
        }
    };

    return { isCalendlyLoaded, openCalendly };
};

export default useCalendly;
